import {EventBus} from '@mintdata-wsp/common/bus/EventBus';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {CreateDesignSnapshotRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/CreateDesignSnapshotRequest';
import {DesignSnapshotResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/DesignSnapshotResponse';
import {DesignSnapshotsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/DesignSnapshotsResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    DesignVersion,
    DesignVersionFilterType,
    DesignVersionId,
    DesignVersionsState
} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignVersionsState';
import {OpenDesignState} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {SnapshotRestoredEvent} from '@mintdata-wsp/wsp/controllers/multiplayer/MultiplayerController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import _find from 'lodash/find';
import {action, runInAction} from 'mobx';

// ----------------------------------------------------------------------

export enum DesignVersionDiffObjectType
{
    SHEET = 'sheet',
    PAGE = 'page',

    GROUP = 'group',
    ITEM = 'item',
}

export enum DesignVersionDiffActionType
{
    NONE = 'none',
    ADDED = 'added',
    REMOVED = 'removed',
    CHANGED = 'changed',
}

export interface DesignVersionDiffText
{
    type?:DesignVersionDiffActionType;
    text:string;
}

export interface DesignVersionDiffChanges
{
    groupName?:string;
    propName?:string;
    range?:string;
    // text?:string;
    texts?:DesignVersionDiffText[];
}

export interface DesignVersionDiff
{
    objectType:DesignVersionDiffObjectType;
    left:DesignVersionDiffChanges;
    right:DesignVersionDiffChanges;

    items?:DesignVersionDiff[];

    lineNumber?:number;

    actionType?:DesignVersionDiffActionType;
    filterType?:DesignVersionFilterType;
}

// ----------------------------------------------------------------------

export class DesignVersionsController
{
    public init()
    {
        // const {multiplayer} = controllers.ui;
        // EventBus.listen(multiplayer, {
        //     onConnected: this._onMultiplayerConnected,
        //     onDisconnected: this._onMultiplayerDisconnected,
        //     onConnectionRestored: this._onMultiplayerConnected,
        // });
    }

    // @bind
    // private _onMultiplayerConnected()
    // {
    //     const {multiplayer} = controllers.ui;
    //     multiplayer.subscribeToEvents('snapshot-restored', this._onSnapshotRestored);
    // }

    // @action.bound
    // private _onMultiplayerDisconnected()
    // {
    //     const state = this.getState();
    //     state.isRestoring = false;
    //     state.restoreVersionId = null;
    // }

    // @action.bound
    // private async _onSnapshotRestored(event:SnapshotRestoredEvent)
    // {
    //     // const state = this.getState();
    //     // state.isRestoring = false;
    //     // state.restoreVersionId = null;
    // }

    public getState():DesignVersionsState | null
    {
        const {designVersions} = rootState.ui.visualDesigner;
        return designVersions ? designVersions : null;
    }

    @bind
    public getVersions():DesignVersion[]
    {
        const state = this.getState();
        return state.versions || [];
    }

    @bind
    public getVersionById(versionId:DesignVersionId):DesignVersion
    {
        const state = this.getState();
        if( !state.currentVersion ) return;

        if( state.currentVersion.id === versionId )
            return state.currentVersion;

        return _find(state.versions, (version:DesignVersion) => version.id === versionId);
    }

    @bind
    public getLeftVersionForDiff():DesignVersion
    {
        const state = this.getState();

        const first:DesignVersion = this.getVersionById(state.selectedVersions[0]);
        const second:DesignVersion = this.getVersionById(state.selectedVersions[1]);

        return first.timestamp < second.timestamp ? first : second;
    }

    @bind
    public getRightVersionForDiff():DesignVersion
    {
        const state = this.getState();

        const first:DesignVersion = this.getVersionById(state.selectedVersions[0]);
        const second:DesignVersion = this.getVersionById(state.selectedVersions[1]);

        return first.timestamp < second.timestamp ? second : first;
    }

    @bind
    public getCurrentVersion():DesignVersion
    {
        const state = this.getState();
        return state.currentVersion;
    }

    @bind
    public getDiff():DesignVersionDiff[]
    {
        const left:DesignVersion = this.getLeftVersionForDiff();
        const right:DesignVersion = this.getRightVersionForDiff();

        return this._diff(left, right);
    }

    // ----------------------------------------------------------------------

    @action.bound
    public toggleFilter(filterName:DesignVersionFilterType):void
    {
        const state = this.getState();

        state.filters[filterName] = !state.filters[filterName];
    }

    @bind
    public getFilterState(filterName:DesignVersionFilterType):boolean
    {
        const state = this.getState();

        return state.filters[filterName];
    }

    // ----------------------------------------------------------------------

    @action.bound
    public async nameCurrentVersion(name:string, description:string):Promise<void>
    {
        const {ui: {openDesign}} = controllers;
        const openDesignState:OpenDesignState = openDesign.getOpenDesign();

        await API.designSnapshots.create(
            openDesignState.designRef.projectId,
            openDesignState.designRef.designId,
            new CreateDesignSnapshotRequest(
                name,
                description
            )
        );

        return this.refresh();
    }

    @action.bound
    public openVersion(versionId:DesignVersionId):void
    {
        const state = this.getState();

        state.openedVersionId = versionId;
    }

    @action.bound
    public async renameVersion(versionId:DesignVersionId, name:string, description:string):Promise<void>
    {
        const {ui: {openDesign}} = controllers;
        const openDesignState:OpenDesignState = openDesign.getOpenDesign();

        await API.designSnapshots.update(
            openDesignState.designRef.projectId,
            openDesignState.designRef.designId,
            versionId,
            new CreateDesignSnapshotRequest(
                name,
                description
            )
        );

        return this.refresh();
    }

    @bind
    public isCanRestore(versionId:DesignVersionId):boolean
    {
        const state = this.getState();

        let isCanRestore:boolean | null = null;

        // TODO
        state.versions.forEach((version:DesignVersion) =>
        {
            if( isCanRestore !== null ) return;

            if( version.id === versionId ) isCanRestore = true;

            // if( !isCanRestore ) return;

            // can restore published version
            if( version.isPublished && version.id !== versionId ) isCanRestore = false;

            // can't restore published version
            // if( version.isPublished ) isCanRestore = false;
        });

        return isCanRestore;
    }

    @action.bound
    public confirmRestoreVersion(versionId:DesignVersionId):void
    {
        const state = this.getState();

        if( !this.isCanRestore(versionId) ) return;

        state.restoreVersionId = versionId;
    }

    @action.bound
    public async restoreVersion():Promise<void>
    {
        const {artifacts, openDesign, flow, multiplayer} = controllers.ui;
        const state = this.getState();
        const {designRef: {projectId, designId}, isMultiplayerMode} = openDesign.getOpenDesign();

        state.isRestoring = true;

        if( isMultiplayerMode )
        {
            await multiplayer.sendEvent('restore-snapshot', {
                snapshotId: state.restoreVersionId
            });
        }
        else
        {
            await API.designSnapshots.restore(
                projectId,
                designId,
                state.restoreVersionId
            );

            flow.clearFlows();

            await artifacts.ensureCloudArtifactsAreLoaded();
            await flow.ensureFlowsAreLoaded(projectId, designId);

            await openDesign.reloadOpenDesign(true);

            runInAction(() =>
            {
                state.isRestoring = false;
                state.restoreVersionId = null;
            });
        }
    }

    @action.bound
    public discardRestoreVersion():void
    {
        const state = this.getState();

        state.restoreVersionId = null;
    }

    @action.bound
    public selectVersionToCompare(versionId:DesignVersionId):void
    {
        const state = this.getState();

        if( this.isSelected(versionId) )
        {
            this._removeVersionFromCompare(versionId);
        }
        else if( state.selectedVersions.length < 2 )
        {
            this._addVersionToCompare(versionId);
        }
        else
        {
            // remove last added
            // but if it current - remove other
            const first = state.selectedVersions[0];
            const second = state.selectedVersions[1];

            this._removeVersionFromCompare(this.isOpened(second) ? first : second);

            this._addVersionToCompare(versionId);
        }

        const isDiffVisible = this.isDiffVisible();
        const isDetailsVisible = this.isDetailsVisible();

        if( isDiffVisible || isDetailsVisible )
        {
            const {length} = state.selectedVersions;

            if( length === 0 )
            {
                this.showDiff(false);
                this.showDetails(false);
            }
            else if( length === 1 )
            {
                this.showDiff(false);
                this.showDetails();
            }
            else if( length === 2 )
            {
                this.showDiff();
                this.showDetails(false);
            }
        }
    }

    @action.bound
    public async deleteVersion(versionId:DesignVersionId):Promise<void>
    {
        const {openDesign} = controllers.ui;
        const state = this.getState();
        const {designRef: {projectId, designId}} = openDesign.getOpenDesign();

        state.isRestoring = true;

        await API.designSnapshots.delete(
            projectId,
            designId,
            versionId
        );

        await this.refresh();

        runInAction(() =>
        {
            state.isRestoring = false;
        });
    }

    @action.bound
    public setOpenedVersion(newVersionId:DesignVersionId):void
    {
        const state = this.getState();

        const oldVersionId = state.openedVersionId;

        if( oldVersionId === newVersionId ) return;

        state.openedVersionId = newVersionId;
    }

    @action.bound
    private _addVersionToCompare(versionId:DesignVersionId):void
    {
        const state = this.getState();

        state.selectedVersions.push(versionId);
    }

    @action.bound
    private _removeVersionFromCompare(versionId:DesignVersionId):void
    {
        const state = this.getState();

        const index = state.selectedVersions.indexOf(versionId);

        if( index === -1 ) return;

        state.selectedVersions.splice(index, 1);
    }

    // ----------------------------------------------------------------------

    @bind
    public isOpened(versionId:DesignVersionId):boolean
    {
        const state = this.getState();
        return state.openedVersionId === versionId;
    }

    @bind
    public isSelected(versionId:DesignVersionId):boolean
    {
        const state = this.getState();
        return state.selectedVersions.indexOf(versionId) !== -1;
    }

    // ----------------------------------------------------------------------

    @action.bound
    public showPanel(visibility:boolean = true):void
    {
        const state = this.getState();

        state.showPanel = visibility;

        if( !visibility )
        {
            this.showDiff(visibility);
            this.showDetails(visibility);
        }
    }

    @action.bound
    public showDiff(visibility:boolean = true):void
    {
        const state = this.getState();

        state.showDiff = visibility;
    }

    @action.bound
    public showDetails(visibility:boolean = true):void
    {
        const state = this.getState();

        state.showDetails = visibility;
    }

    // ----------------------------------------------------------------------

    @bind
    public isVersionsPanelVisible():boolean
    {
        const state = this.getState();

        return state.showPanel;
    }

    @bind
    public isDiffVisible():boolean
    {
        const state = this.getState();

        return state.showDiff;
    }

    @bind
    public isDetailsVisible():boolean
    {
        const state = this.getState();

        return state.showDetails;
    }

    // ----------------------------------------------------------------------

    @bind
    public toggleVersionsPanelVisibility():void
    {
        const state = this.getState();

        this.showPanel(!state.showPanel);
    }

    // ----------------------------------------------------------------------

    @bind
    private _getVersionId(id?:number):string
    {
        const state = this.getState();
        const openDesign = commonControllers.designState.getOpenDesign();
        const {designId} = openDesign.designRef;
        const nextIndex = id !== null ? id : state.versions.length;

        return designId + '_' + nextIndex;
    }

    @bind
    public getNextVersionName():string
    {
        const state = this.getState();

        let maxXX:number = 0;
        let maxYY:number = 0;

        const versionTemplateRe = /Version (\d+)\.(\d+)/;

        state.versions.forEach((version:DesignVersion) =>
        {
            const match = versionTemplateRe.exec(version.name);

            if( !match ) return;

            let [all, x, y] = match;

            let xx = parseInt(x);
            let yy = parseInt(y);

            if( yy === 99 )
            {
                yy = 0;
                xx += 1;
            }
            else
            {
                yy += 1;
            }

            if( xx > maxXX )
            {
                maxXX = xx;
                maxYY = yy;
            }
            else if( xx == maxXX && yy > maxYY )
            {
                maxYY = yy;
            }
        });

        if( maxXX === 0 && maxYY === 0 )
        {
            maxYY = 1;
        }

        return `Version ${maxXX}.${maxYY < 10 ? '0' + maxYY : maxYY}`;
    }

    @bind
    private _getNextVersionId():string
    {
        const state = this.getState();

        return this._getVersionId(state.versions.length + 1);
    }

    @action.bound
    private addVersion(version:DesignVersion):void
    {
        const state = this.getState();

        state.versions.unshift(version);
    }

    // ----------------------------------------------------------------------

    @action.bound
    public async refresh()
    {
        const {ui: {openDesign}} = controllers;
        const state = this.getState();
        const openDesignState:OpenDesignState = openDesign.getOpenDesign();

        state.currentVersion = new DesignVersion(
            IdUtil.newGUID(),
            'Current Version',
            '',
            Date.now(),
            rootState.userSession.currentUser.fullName,
            true,
            false
        );

        const snapshotsResponce:DesignSnapshotsResponse = await API.designSnapshots.list(
            openDesignState.designRef.projectId,
            openDesignState.designRef.designId
        );

        runInAction(() =>
        {
            state.versions = [];

            snapshotsResponce.designSnapshots.forEach((designSnapshot:DesignSnapshotResponse) =>
            {
                this.addVersion(
                    new DesignVersion(
                        String(designSnapshot.id),
                        designSnapshot.name,
                        designSnapshot.description,
                        designSnapshot.createdAt,
                        designSnapshot.fullName,
                        false,
                        false,
                    )
                );
            });
        });
    }

    @action.bound
    public initSamples()
    {
        const state = this.getState();

        if( state.versions.length ) return;

        // ----------------------------------------------------------------------

        const dateNow = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;

        const fakeDate = (index:number) => dateNow - oneDay * index - Math.ceil(Math.random() * oneDay);

        // ----------------------------------------------------------------------

        // const openDesign = commonControllers.designState.getOpenDesign();
        //
        // // const designId =  UniqueId.fromString(openDesign.designRef.uuid);
        // const {designId} = openDesign.designRef;
        //
        // const this._getVersionId = (index:number) => designId + '_' + index;

        // ----------------------------------------------------------------------

        state.currentVersion =
            new DesignVersion(
                this._getVersionId(0),
                'Current Version',
                '',
                Date.now(),
                'TODO: current author',
                true,
                false,
            );

        this.addVersion(
            new DesignVersion(
                this._getNextVersionId(),
                'Initial Version',
                'Start new project: Hallo World',
                fakeDate(4),
                'Denis Denis',
                false,
                false,
            )
        );

        this.addVersion(
            new DesignVersion(
                this._getNextVersionId(),
                'Version 1',
                'Pages and Viewport: fix some bugs',
                fakeDate(3),
                'Igor Igor',
                false,
                false,
            )
        );

        this.addVersion(
            new DesignVersion(
                this._getNextVersionId(),
                'Dev Release',
                'Fixed ALL',
                fakeDate(2),
                'Viktor',
                false,
                true,
            )
        );

        this.addVersion(
            new DesignVersion(
                this._getNextVersionId(),
                'Version 2',
                'opanki...',
                fakeDate(1),
                'Denis',
                false,
                false,
            )
        );

        this.setOpenedVersion(this._getVersionId(0));
        this.selectVersionToCompare(this._getVersionId(0));
        // this.selectVersionToCompare(this._getVersionId(3));

        // this.showDiff();
    }

    private _diff(left:DesignVersion, right:DesignVersion):DesignVersionDiff[]
    {
        const diff:DesignVersionDiff[] = [];

        diff.push({
            objectType: DesignVersionDiffObjectType.SHEET,
            actionType: DesignVersionDiffActionType.NONE,
            left: {
                texts: [
                    {
                        text: 'Sheet 1'
                    },
                ],
            },
            right: {
                texts: [
                    {
                        text: 'Sheet 1'
                    },
                ],
            },
        });

        diff.push({
            objectType: DesignVersionDiffObjectType.SHEET,
            actionType: DesignVersionDiffActionType.ADDED,
            left: null,
            right: {
                texts: [
                    {
                        // type: DesignVersionDiffActionType.ADDED,
                        text: 'Sheet 2'
                    },
                ],
            },
        });

        diff.push({
            objectType: DesignVersionDiffObjectType.SHEET,
            actionType: DesignVersionDiffActionType.REMOVED,
            left: {
                texts: [
                    {
                        text: 'Sheet 3'
                    },
                ],
            },
            right: null,
            items: [
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.NOTES,
                    left: {groupName: 'NOTES'},
                    right: {groupName: 'NOTES'},
                    items: [
                        {
                            lineNumber: 1,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.NOTES,
                            actionType: DesignVersionDiffActionType.NONE,
                            left: {
                                range: '@A1',
                                propName: 'Notes',
                                texts: [
                                    {
                                        text: 'Choose Numbers > Preferences (from the Numbers menu at the top of your screen). Click General. Click the Text Size pop-up menu, then choose a size.'
                                    },
                                ],
                            },
                            right: {
                                range: '@A1',
                                propName: 'Notes',
                                texts: [
                                    {
                                        text: 'Choose Numbers > Preferences (from the Numbers menu at the top of your screen). Click General. Click the Text Size pop-up menu, then choose a size.'
                                    },
                                ],
                            },
                        },
                        {
                            lineNumber: 2,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.NOTES,
                            actionType: DesignVersionDiffActionType.REMOVED,
                            left: {
                                range: '@MyRange',
                                propName: 'Notes',
                                texts: [
                                    {
                                        // type: DesignVersionDiffActionType.REMOVED, // for left side
                                        text: 'Choose Numbers > Preferences (from the Numbers menu at the top of your screen). Click General. Click the Text Size pop-up menu, then choose a size.'
                                    },
                                ],
                            },
                            right: null,
                        },
                        {
                            lineNumber: 3,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.NOTES,
                            actionType: DesignVersionDiffActionType.ADDED,
                            left: null,
                            right: {
                                range: '@MyRange',
                                propName: 'Notes',
                                texts: [
                                    {
                                        // type: DesignVersionDiffActionType.ADDED,
                                        text: 'Choose Numbers > Preferences (from the Numbers menu at the top of your screen). Click General. Click the Text Size pop-up menu, then choose a size.'
                                    },
                                ],
                            },
                        },
                        {
                            lineNumber: 4,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.NOTES,
                            actionType: DesignVersionDiffActionType.CHANGED,
                            left: {
                                range: '@A7',
                                propName: 'Notes',
                                texts: [
                                    {
                                        text: 'Choose Numbers > Preferences (from the Numbers menu at the top of your screen).'
                                    },
                                ],
                            },
                            right: {
                                range: '@A7',
                                propName: 'Notes',
                                texts: [
                                    {
                                        type: DesignVersionDiffActionType.REMOVED,
                                        text: 'Choose Numbers > '
                                    },
                                    {
                                        text: 'Preferences (from the Numbers menu at the top of your screen).'
                                    },
                                    {
                                        type: DesignVersionDiffActionType.ADDED,
                                        text: 'Click General. Click the Text Size pop-up menu, then choose a size.'
                                    },
                                ],
                            },
                        },
                    ],
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.EVENT_BINDINGS,
                    left: {groupName: 'EVENT BINDINGS'},
                    right: {groupName: 'EVENT BINDINGS'},
                    items: [
                        {
                            lineNumber: 5,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.EVENT_BINDINGS,
                            actionType: DesignVersionDiffActionType.NONE,
                            left: {
                                range: '@A24',
                                propName: 'onClick',
                                texts: [
                                    {
                                        text: 'Main Page - Save Button'
                                    },
                                ],
                            },
                            right: {
                                range: '@A24',
                                propName: 'onClick',
                                texts: [
                                    {
                                        text: 'Main Page - Save Button'
                                    },
                                ],
                            },
                        },
                        {
                            lineNumber: 6,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.EVENT_BINDINGS,
                            actionType: DesignVersionDiffActionType.NONE,
                            left: {
                                range: '@MyRange',
                                propName: 'onEnter',
                                texts: [
                                    {
                                        text: 'Main Page - Save Button'
                                    },
                                ],
                            },
                            right: {
                                range: '@MyRange',
                                propName: 'onEnter',
                                texts: [
                                    {
                                        text: 'Main Page - Save Button'
                                    },
                                ],
                            },
                        },
                    ],
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.PROP_BINDINGS,
                    left: {groupName: 'PROPERTY BINDINGS'},
                    right: {groupName: 'PROPERTY BINDINGS'},
                    items: [
                        {
                            lineNumber: 7,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.PROP_BINDINGS,
                            actionType: DesignVersionDiffActionType.NONE,
                            left: {
                                range: '@B12',
                                propName: 'Caption',
                                texts: [
                                    {
                                        text: 'Main Page - Page title Label'
                                    },
                                ],
                            },
                            right: {
                                range: '@B12',
                                propName: 'Caption',
                                texts: [
                                    {
                                        text: 'Main Page - Page title Label'
                                    },
                                ],
                            },
                        },
                        {
                            lineNumber: 8,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.PROP_BINDINGS,
                            actionType: DesignVersionDiffActionType.NONE,
                            left: {
                                range: '@MyRange',
                                propName: 'Text - Font Color',
                                texts: [
                                    {
                                        text: 'Main Page - Page title Label'
                                    },
                                ],
                            },
                            right: {
                                range: '@MyRange',
                                propName: 'Text - Font Color',
                                texts: [
                                    {
                                        text: 'Main Page - Page title Label'
                                    },
                                ],
                            },
                        },],
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.NAMED_RANGES,
                    left: {groupName: 'NAMED RANGES'},
                    right: {groupName: 'NAMED RANGES'},
                    items: [
                        {
                            lineNumber: 8,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.NAMED_RANGES,
                            actionType: DesignVersionDiffActionType.NONE,
                            left: {
                                range: '@MyRange',
                                propName: '@B2:B20',
                                texts: [
                                    {
                                        text: 'Down - Format: On - Locked'
                                    },
                                ],
                            },
                            right: {
                                range: '@MyRange',
                                propName: '@B2:B20',
                                texts: [
                                    {
                                        text: 'Down - Format: On - Locked'
                                    },
                                ],
                            },
                        },
                    ],
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.CELLS,
                    left: {groupName: 'CELLS'},
                    right: {groupName: 'CELLS'},
                    items: [
                        {
                            lineNumber: 9,
                            objectType: DesignVersionDiffObjectType.ITEM,
                            filterType: DesignVersionFilterType.CELLS,
                            actionType: DesignVersionDiffActionType.NONE,
                            left: {
                                range: '@A1',
                                propName: 'Non Lazy',
                                texts: [
                                    {
                                        text: '123 - f=SUM(B12:B47) - 123 rows'
                                    },
                                ],
                            },
                            right: {
                                range: '@A1',
                                propName: 'Non Lazy',
                                texts: [
                                    {
                                        text: '123 - f=SUM(B12:B47) - 123 rows'
                                    },
                                ],
                            },
                        },
                    ],
                },
            ],
        });

        diff.push({
            objectType: DesignVersionDiffObjectType.SHEET,
            actionType: DesignVersionDiffActionType.CHANGED,
            left: {
                texts: [
                    {
                        text: 'Sheet 4'
                    },
                ],
            },
            right: {
                texts: [
                    {
                        type: DesignVersionDiffActionType.CHANGED,
                        text: 'Main App Logic'
                    },
                ],
            },
            items: [
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.NOTES,
                    left: {groupName: 'NOTES'},
                    right: {groupName: 'NOTES'},
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.EVENT_BINDINGS,
                    left: {groupName: 'EVENT BINDINGS'},
                    right: {groupName: 'EVENT BINDINGS'},
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.PROP_BINDINGS,
                    left: {groupName: 'PROPERTY BINDINGS'},
                    right: {groupName: 'PROPERTY BINDINGS'},
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.NAMED_RANGES,
                    left: {groupName: 'NAMED RANGES'},
                    right: {groupName: 'NAMED RANGES'},
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.CELLS,
                    left: {groupName: 'CELLS'},
                    right: {groupName: 'CELLS'},
                },
            ],
        });

        diff.push({
            objectType: DesignVersionDiffObjectType.PAGE,
            actionType: DesignVersionDiffActionType.NONE,
            left: {
                texts: [
                    {
                        text: 'Page 1'
                    },
                ],
            },
            right: {
                texts: [
                    {
                        text: 'Page 1'
                    },
                ],
            },
        });

        diff.push({
            objectType: DesignVersionDiffObjectType.PAGE,
            actionType: DesignVersionDiffActionType.ADDED,
            left: null,
            right: {
                texts: [
                    {
                        // type: DesignVersionDiffActionType.ADDED, // for page
                        text: 'Page 2'
                    },
                ],
            },
        });

        diff.push({
            objectType: DesignVersionDiffObjectType.PAGE,
            actionType: DesignVersionDiffActionType.REMOVED,
            left: {
                texts: [
                    {
                        // type: DesignVersionDiffActionType.REMOVED, // left side
                        text: 'Page 3'
                    },
                ],
            },
            right: null,
        });

        diff.push({
            objectType: DesignVersionDiffObjectType.PAGE,
            actionType: DesignVersionDiffActionType.CHANGED,
            left: {
                texts: [
                    {
                        text: 'Home Page'
                    },
                ],
            },
            right: {
                texts: [
                    {
                        type: DesignVersionDiffActionType.CHANGED,
                        text: 'APP'
                    },
                    {
                        text: ' Page'
                    },
                ],
            },
            items: [
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.EVENT_BINDINGS,
                    left: {groupName: 'EVENT BINDINGS'},
                    right: {groupName: 'EVENT BINDINGS'},
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.PROP_BINDINGS,
                    left: {groupName: 'PROPERTY BINDINGS'},
                    right: {groupName: 'PROPERTY BINDINGS'},
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.COMPONENTS,
                    left: {groupName: 'COMPONENTS'},
                    right: {groupName: 'COMPONENTS'},
                },
                {
                    objectType: DesignVersionDiffObjectType.GROUP,
                    filterType: DesignVersionFilterType.POSITIONS,
                    left: {groupName: 'POSITIONS'},
                    right: {groupName: 'POSITIONS'},
                },
            ],
        });

        return diff;
    }

}
